import type { CSSProperties } from 'react';
import { DEFAULT_APPEARANCE, type AppearanceFields } from '@/features/themes/lib/appearance';

export interface PublicBranding extends Partial<AppearanceFields> {
  app_name?: string | null;
  logo_url?: string | null;
  browser_title?: string | null;
  favicon_url?: string | null;
  social_image_url?: string | null;
  custom_head_code?: string | null;
  custom_body_start_code?: string | null;
  custom_body_end_code?: string | null;
  google_analytics_measurement_id?: string | null;
  default_page_template_id?: string | null;
  default_post_template_id?: string | null;
}

export function getTemplateIdForContent(
  branding: PublicBranding | null | undefined,
  individualTemplateId: string | null | undefined,
  contentKind: 'page' | 'post',
): string | null {
  if (individualTemplateId) return individualTemplateId;
  return contentKind === 'post' ? branding?.default_post_template_id ?? null : branding?.default_page_template_id ?? null;
}

// layout_mode vem do banco como `text` com `check (layout_mode in ('boxed', 'full'))`
// (ver 20260708003000_appearance_templates.sql) — o tipo gerado do Supabase não
// reflete o check constraint, então aceitamos `string | null` aqui e confiamos
// na restrição do banco em vez de forçar o literal 'boxed' | 'full' no tipo.
export type PublicTemplate =
  | ({ [K in keyof Omit<AppearanceFields, 'layout_mode'>]?: AppearanceFields[K] | null } & { layout_mode?: string | null })
  | null
  | undefined;

/**
 * Resolve a aparência efetiva de um conteúdo: campos do modelo (quando
 * vinculado e não-nulos) vencem, caindo para o branding do site, caindo para
 * os defaults hardcoded. Nunca deixa um campo undefined.
 */
export function resolveAppearance(
  branding: PublicBranding | null | undefined,
  template: PublicTemplate,
): AppearanceFields {
  const pick = <K extends keyof AppearanceFields>(key: K): AppearanceFields[K] =>
    (template?.[key] as AppearanceFields[K] | undefined) ??
    (branding?.[key] as AppearanceFields[K] | undefined) ??
    DEFAULT_APPEARANCE[key];

  return {
    primary_color: pick('primary_color'),
    accent_color: pick('accent_color'),
    heading_font: pick('heading_font'),
    body_font: pick('body_font'),
    heading_color: pick('heading_color'),
    body_color: pick('body_color'),
    heading_font_size: pick('heading_font_size'),
    body_font_size: pick('body_font_size'),
    layout_mode: pick('layout_mode'),
    bg_color: pick('bg_color'),
    bg_image_url: pick('bg_image_url'),
    box_color: pick('box_color'),
    box_image_url: pick('box_image_url'),
    box_width: pick('box_width'),
    box_radius: pick('box_radius'),
    page_padding: pick('page_padding'),
    page_margin: pick('page_margin'),
    show_post_date: pick('show_post_date'),
  };
}

/**
 * Deriva CSS custom properties a partir da aparência resolvida, para aplicar
 * num wrapper do site público. Escopar as vars num elemento (em vez de mutar
 * document.documentElement) evita que as cores/fontes do tenant vazem para o
 * admin dentro da mesma sessão SPA. Respeita a regra 10 (cores via tokens).
 */
export function appearanceCssVars(config: AppearanceFields): CSSProperties {
  const vars: Record<string, string> = {
    '--color-primary': config.primary_color,
    '--color-accent': config.accent_color,
    '--color-heading': config.heading_color,
    '--color-paragraph': config.body_color,
    '--font-size-heading': `${config.heading_font_size}px`,
    '--font-size-paragraph': `${config.body_font_size}px`,
    '--font-heading': `'${config.heading_font}', var(--font-display)`,
    '--font-paragraph': `'${config.body_font}', var(--font-body)`,
    '--color-page-bg': config.bg_color,
    '--color-box-bg': config.box_color,
    '--bg-image-page': config.bg_image_url ? `url(${config.bg_image_url})` : 'none',
    '--bg-image-box': config.box_image_url ? `url(${config.box_image_url})` : 'none',
    '--box-width': `${config.box_width}px`,
    '--box-radius': `${config.box_radius}px`,
    '--page-padding': `${config.page_padding}px`,
    '--page-margin': `${config.page_margin}px`,
  };
  return vars;
}
